/* AUTH CONFIRM v2.1 - CACHE BUST: 20260526-signin-onboarding-fix */
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  AlertTriangle,
  Shield,
  Lock
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import {
  isGenericPostAuthLanding,
  POST_AUTH_LANDING_PATH,
  resolvePostAuthRedirect,
} from '../app/router/postAuthLanding';
import { markOnboardingCompleteForSignin } from '../modules/onboarding/markOnboardingCompleteForSignin';
import { collapseSidebarForPostLogin } from '../modules/shell/sidebarState';
import { supabase } from '../lib/supabase';
import { logEvent } from '../services/loggingService';
import { attributeSignupIfPending } from '../services/referralAttribution';
import { attributeOutreachSignupIfPending } from '../services/outreachAttribution';

import LoadingSpinner from './LoadingSpinner';

/**
 * Fires a welcome email using fetch with keepalive:true so the request
 * survives an immediate window.location.replace() navigation.
 */
function fireWelcomeEmail(userId: string, email: string, language: string) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  supabase.auth.getSession().then(({ data: { session } }) => {
    fetch(`${supabaseUrl}/functions/v1/send-funnel-email`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${session?.access_token ?? anonKey}`,
      },
      body: JSON.stringify({ type: 'welcome', user_id: userId, email, language }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({})) as {
          success?: boolean;
          skipped?: boolean;
          resend_id?: string;
          error?: string;
        };
        if (r.ok && data.success && data.resend_id) {
          console.log('[Email Funnel] Welcome email delivered to Resend ✅', data.resend_id);
        } else if (r.ok && data.skipped) {
          console.log('[Email Funnel] Welcome email skipped (already sent)');
        } else {
          console.warn('[Email Funnel] Welcome email not sent:', data.error ?? r.status);
        }
      })
      .catch(e => console.warn('[Email Funnel] Welcome email failed:', e));
  });
}

interface AuthConfirmPageProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'forgot-password' | 'reset-password') => void;
}

const AuthConfirmPage: React.FC<AuthConfirmPageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string | null>(null);
  
  // Detect flow type immediately from URL to avoid showing any UI for OAuth
  const detectInitialFlowType = (): 'oauth' | 'recovery' | 'unknown' => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    
    const code = urlParams.get('code');
    const accessToken = urlParams.get('access_token') || hashParams.get('access_token');
    const type = urlParams.get('type');
    const tokenHash = urlParams.get('token_hash') || hashParams.get('token_hash');
    
    const hasOAuth = code || accessToken || window.location.hash.includes('access_token');
    const hasRecovery = type === 'recovery' && tokenHash;
    
    if (hasOAuth) return 'oauth';
    if (hasRecovery) return 'recovery';
    return 'unknown';
  };
  
  const [flowType, setFlowType] = useState<'oauth' | 'recovery' | 'unknown'>(detectInitialFlowType());

  useEffect(() => {
    let isMounted = true;
    
    const handleAuthConfirm = async () => {
      try {
        console.log('🔐 AUTH CONFIRM: Starting authentication check')
        console.log('🔐 AUTH CONFIRM: Current URL:', window.location.href)
        
        // PRIORITY 1: Check if there's already an active session (most common case for OAuth)
        const { data: { session: existingSession }, error: existingSessionError } = await supabase.auth.getSession();
        
        if (existingSession && !existingSessionError) {
          console.log('🔐 AUTH CONFIRM: Active session already exists - checking flow type');
          
          // CRITICAL: Check localStorage FIRST before URL params
          const oauthIntentedAction = localStorage.getItem('oauth_intended_action');
          console.log('🔐 AUTH CONFIRM: localStorage oauth_intended_action:', oauthIntentedAction);
          
          // Check if this is a password reset flow by looking at URL params
          const urlParams = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const type = urlParams.get('type');
          const tokenHash = urlParams.get('token_hash');
          const code = urlParams.get('code');
          const accessToken = urlParams.get('access_token') || hashParams.get('access_token');
          
          const isPasswordReset = type === 'recovery' && tokenHash;
          const isOAuth = code || accessToken || window.location.hash.includes('access_token');
          
          // CRITICAL: OAuth intent in localStorage means this IS OAuth flow
          // (LinkedIn OAuth is SO fast that the session exists before we can check the URL)
          const hasOAuthIntent = !!oauthIntentedAction;
          const isOAuthFlow = isOAuth || hasOAuthIntent;
          
          console.log('🔐 AUTH CONFIRM: Flow detection with existing session:', {
            isPasswordReset,
            isOAuth,
            hasOAuthIntent,
            isOAuthFlow
          });
          
          if (!isPasswordReset) {
            const intendedAction = (localStorage.getItem('oauth_intended_action') as 'signin' | 'signup') || 'signin';
            // If it's OAuth, validate the action
            if (isOAuthFlow) {
              console.log('🔐 AUTH CONFIRM: OAuth with existing session - validating');
              console.log('🔐 AUTH CONFIRM: Importing OAuth handler...');
              
              // Import and call OAuth handler
              const { handleOAuthCallback } = await import('./Auth/services/oauthAuth');
              
              console.log('🔐 AUTH CONFIRM: Calling handleOAuthCallback with action:', intendedAction);
              const result = await handleOAuthCallback(intendedAction);
              console.log('🔐 AUTH CONFIRM: OAuth callback result:', result);
              
              localStorage.removeItem('oauth_intended_action');
              
              if (!result.success) {
                console.error('🔐 AUTH CONFIRM: OAuth validation failed:', result.error);
                
                // Sign out the OAuth session since validation failed
                await supabase.auth.signOut();
                
                // Store error in sessionStorage so signup page can display it
                sessionStorage.setItem('oauth_error', result.error || 'Authentication failed');
                sessionStorage.setItem('oauth_error_email', existingSession?.user?.email || '');
                
                // Redirect back to signup page to show error
                console.log('🔐 AUTH CONFIRM: Redirecting to signup with error');
                window.location.href = '/signup';
                return;
              }
              
              console.log('🔐 AUTH CONFIRM: OAuth validation passed');
              
              // CRITICAL FIX: For SIGN-IN users, mark onboarding as complete
              // This prevents the onboarding wizard from triggering for returning users
              if (intendedAction === 'signin' && existingSession?.user?.id) {
                console.log('🔐 AUTH CONFIRM: Sign-in flow - marking onboarding complete to skip wizard');
                await markOnboardingCompleteForSignin(existingSession.user.id);
              }
            }
            
            // Clear any password reset flags
            sessionStorage.removeItem('mandatory_password_reset');
            sessionStorage.removeItem('password_reset_in_progress');
            sessionStorage.removeItem('recovery_token_hash');
            sessionStorage.removeItem('recovery_session_id');
            sessionStorage.removeItem('reset_token_hash');
            sessionStorage.removeItem('reset_target_email');
            
            await logEvent({
              level: 'info',
              action: 'auth_confirm_redirect',
              details: { 
                provider: existingSession.user.app_metadata?.provider || 'unknown',
                userId: existingSession.user.id,
                email: existingSession.user.email
              }
            });
            
            // FIXED: Set OAuth completion flag before redirect (same as new session path)
            console.log('🔐 AUTH CONFIRM: Validated - checking for redirect destination');
            sessionStorage.setItem('oauth_just_completed', Date.now().toString());
            collapseSidebarForPostLogin();
            
            // Clean up OAuth in-progress flags
            sessionStorage.removeItem('oauth_in_progress');
            sessionStorage.removeItem('oauth_provider');
            sessionStorage.removeItem('oauth_start_time');
            
              console.log('🔐 AUTH CONFIRM: Set OAuth completion flag for AppStateManager');
              
              // Check if there's a pending redirect (e.g., from guest optimization flow)
              const redirectPath = sessionStorage.getItem('redirect_after_login');
              
              console.log('🔍 [REDIRECT CHECK] Auth confirmed - checking redirect:', {
                hasRedirectPath: !!redirectPath,
                redirectPath: redirectPath,
                intendedAction: intendedAction,
                allSessionStorageKeys: Object.keys(sessionStorage),
                sessionStorageContent: JSON.stringify(Object.fromEntries(Object.keys(sessionStorage).map(k => [k, sessionStorage.getItem(k)])))
              });
              
              // Always fire welcome email for signups BEFORE deciding where to redirect.
              // Uses fetch with keepalive:true so the request survives page navigation.
              if ((intendedAction === 'signup' || type === 'signup') && existingSession?.user?.email) {
                console.log('[Email Funnel] Signup detected — firing welcome email to:', existingSession.user.email);
                fireWelcomeEmail(
                  existingSession.user.id,
                  existingSession.user.email,
                  localStorage.getItem('i18nextLng')?.split('-')[0] || 'en',
                );
                // TASK 7-2 — best-effort referral attribution. Awaited here
                // (very short RPC) so the row exists before the user hits
                // the dashboard. Never blocks longer than the RPC itself.
                try { await attributeSignupIfPending(); } catch { /* noop */ }
                try { await attributeOutreachSignupIfPending(); } catch { /* noop */ }
              }

              if (redirectPath) {
                console.log('🔐 AUTH CONFIRM: ✅ Found redirect_after_login, redirecting to:', redirectPath);
                sessionStorage.removeItem('redirect_after_login');
                // For signups with a stale redirect, go to consent first then let consent redirect to the stored path
                if (intendedAction === 'signup' || type === 'signup') {
                  if (redirectPath !== '/profile' && !isGenericPostAuthLanding(redirectPath)) {
                    sessionStorage.setItem('redirect_after_login', redirectPath);
                  }
                  window.location.replace('/signup/consent');
                } else {
                  window.location.replace(resolvePostAuthRedirect(redirectPath));
                }
              } else if (intendedAction === 'signup' || type === 'signup') {
                // If user came from pricing (plan + billing), start payment flow then redirect to Cashfree
                const pricingPlanRaw = sessionStorage.getItem('pricing_plan_after_auth');
                if (pricingPlanRaw && existingSession?.user) {
                  try {
                    const { planId: urlPlanId, billing } = JSON.parse(pricingPlanRaw) as { planId: string; billing: string };
                    sessionStorage.removeItem('pricing_plan_after_auth');
                    const planIdMap: Record<string, string> = { basic: 'professional', professional: 'premium' };
                    const subscriptionPlanId = planIdMap[urlPlanId] || urlPlanId;
                    const { createSubscription } = await import('../services/subscriptionService.cashfree');
                    const { data: userData } = await supabase.from('users').select('email, phone').eq('user_id', existingSession.user.id).single();
                    const result = await createSubscription({
                      plan_id: subscriptionPlanId as 'professional' | 'premium' | 'enterprise',
                      billing_cycle: billing as 'monthly' | 'annual',
                      currency: 'INR',
                      customer_email: existingSession.user.email || userData?.email || '',
                      customer_phone: userData?.phone || '9999999999',
                    });
                    if (result.success && result.short_url) {
                      window.location.href = result.short_url;
                      return;
                    }
                  } catch (e) {
                    console.warn('[AuthConfirm] Pricing payment flow failed, continuing to consent:', e);
                  }
                }
                // New signup: mandatory Privacy Policy Consent before agent landing
                console.log('🔐 AUTH CONFIRM: Signup flow - redirecting to Privacy Consent');
                window.location.replace('/signup/consent');
              } else {
                // Login (signin): if user came from pricing with plan, trigger payment then Cashfree
                const pricingPlanRaw = sessionStorage.getItem('pricing_plan_after_auth');
                if (pricingPlanRaw && existingSession?.user) {
                  try {
                    const { planId: urlPlanId, billing } = JSON.parse(pricingPlanRaw) as { planId: string; billing: string };
                    sessionStorage.removeItem('pricing_plan_after_auth');
                    const planIdMap: Record<string, string> = { basic: 'professional', professional: 'premium' };
                    const subscriptionPlanId = planIdMap[urlPlanId] || urlPlanId;
                    const { createSubscription } = await import('../services/subscriptionService.cashfree');
                    const { data: userData } = await supabase.from('users').select('email, phone').eq('user_id', existingSession.user.id).single();
                    const result = await createSubscription({
                      plan_id: subscriptionPlanId as 'professional' | 'premium' | 'enterprise',
                      billing_cycle: billing as 'monthly' | 'annual',
                      currency: 'INR',
                      customer_email: existingSession.user.email || userData?.email || '',
                      customer_phone: userData?.phone || '9999999999',
                    });
                    if (result.success && result.short_url) {
                      window.location.href = result.short_url;
                      return;
                    }
                  } catch (e) {
                    console.warn('[AuthConfirm] Pricing payment flow (login) failed:', e);
                  }
                }
                console.log('🔐 AUTH CONFIRM: ⚠️ No redirect path found - defaulting to agent');
                window.location.replace(POST_AUTH_LANDING_PATH);
              }
              return;
          }
          
          // If we're here, it's a password reset with an existing session
          console.log('🔐 AUTH CONFIRM: Password reset detected with existing session');
        }
        
        // PRIORITY 2: Check URL parameters to determine flow type
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        // PRIORITY 2.1: Check for OAuth errors FIRST (before checking for valid flows)
        const error = urlParams.get('error') || hashParams.get('error');
        const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');
        
        if (error) {
          console.log('🔐 AUTH CONFIRM: OAuth error detected:', error, errorDescription);
          
          // CRITICAL FIX: Handle OAuth cancellation (user clicked cancel/back)
          if (error === 'access_denied' || error.includes('cancel')) {
            console.log('🔐 AUTH CONFIRM: User cancelled OAuth - redirecting to home');
            
            // Clean up OAuth state
            sessionStorage.removeItem('oauth_in_progress');
            sessionStorage.removeItem('oauth_provider');
            sessionStorage.removeItem('oauth_start_time');
            localStorage.removeItem('oauth_intended_action');
            
            // Redirect to homepage (NOT login page to avoid confusion)
            window.location.href = '/';
            return;
          }
          
          // Decode error message (replace + with spaces, decode URL encoding)
          const decodedError = decodeURIComponent(errorDescription || error).replace(/\+/g, ' ');
          
          // Transform database errors into user-friendly messages
          let userFriendlyError = decodedError;
          
          if (decodedError.toLowerCase().includes('database error saving') || 
              decodedError.toLowerCase().includes('user already exists') ||
              decodedError.toLowerCase().includes('duplicate')) {
            
            // Extract email from session if available
            const { data: { session } } = await supabase.auth.getSession();
            const userEmail = session?.user?.email || 'this email';
            
            // Check if this was a signup attempt
            const intendedAction = localStorage.getItem('oauth_intended_action');
            
            if (intendedAction === 'signup') {
              userFriendlyError = `An account with ${userEmail} already exists. Please sign in instead.`;
            } else {
              userFriendlyError = `Unable to sign in. Please try again or contact support if the issue persists.`;
            }
          }
          
          console.log('🔐 AUTH CONFIRM: Transformed error:', userFriendlyError);
          sessionStorage.setItem('oauth_error', userFriendlyError);
          
          // Clean up OAuth state
          sessionStorage.removeItem('oauth_in_progress');
          localStorage.removeItem('oauth_intended_action');
          
          // CRITICAL FIX: Always redirect to homepage with error stored in sessionStorage
          // The Auth component will display the error
          console.log('🔐 AUTH CONFIRM: Redirecting to home with error');
          window.location.href = '/';
          return;
        }
        
        const tokenHash = urlParams.get('token_hash') || hashParams.get('token_hash');
        const type = urlParams.get('type') || hashParams.get('type');
        const code = urlParams.get('code');
        const accessToken = urlParams.get('access_token') || hashParams.get('access_token');
        
        // Determine if this is OAuth or password recovery
        const isOAuthCallback = code || accessToken || window.location.hash.includes('access_token') || window.location.hash.includes('provider_token');
        const isRecoveryFlow = type === 'recovery' && tokenHash;
        
        console.log('🔐 AUTH CONFIRM: Flow detection:', {
          isOAuthCallback,
          isRecoveryFlow,
          hasCode: !!code,
          hasAccessToken: !!accessToken,
          hasExistingSession: !!existingSession
        });
        
        // For OAuth flows, handle with proper verification and validation
        if (isOAuthCallback) {
          console.log('🔐 AUTH CONFIRM: OAuth detected - processing with validation');
          console.log('🔐 AUTH CONFIRM: No existing session - creating new session');
          
          // CRITICAL: Supabase uses PKCE by default — redirect has ?code=... and no hash.
          // We must exchange the code for a session; otherwise getSession() stays null and sign-in fails.
          const code = urlParams.get('code');
          if (code) {
            console.log('🔐 AUTH CONFIRM: PKCE flow — exchanging code for session');
            try {
              const { data: exchangeData, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
              if (exchangeError) {
                console.error('🔐 AUTH CONFIRM: PKCE exchange failed:', exchangeError);
                if (isMounted) {
                  setStatus('error');
                  setError(exchangeError.message || 'Sign-in could not be completed. Please try again.');
                }
                return;
              }
              if (exchangeData?.session) {
                console.log('🔐 AUTH CONFIRM: PKCE exchange successful — session established');
              }
            } catch (exchangeErr) {
              console.error('🔐 AUTH CONFIRM: PKCE exchange error:', exchangeErr);
              if (isMounted) {
                setStatus('error');
                setError('Sign-in could not be completed. Please try again.');
              }
              return;
            }
          }
          
          // Import the OAuth handler (dynamic import)
          console.log('🔐 AUTH CONFIRM: Importing OAuth handler...');
          const { handleOAuthCallback } = await import('./Auth/services/oauthAuth');
          
          // Get the intended action from localStorage
          const intendedAction = (localStorage.getItem('oauth_intended_action') as 'signin' | 'signup') || 'signin';
          console.log('🔐 AUTH CONFIRM: Intended action:', intendedAction);
          
          // Process OAuth callback with validation
          console.log('🔐 AUTH CONFIRM: Calling handleOAuthCallback...');
          const result = await handleOAuthCallback(intendedAction);
          console.log('🔐 AUTH CONFIRM: OAuth callback result:', result);
          
          // Log to debug-logs
          await logEvent({
            level: 'info',
            action: 'oauth_callback_completed',
            details: { success: result.success, isNewUser: result.isNewUser }
          });
          
          // Clean up
          localStorage.removeItem('oauth_intended_action');
          
          if (!result.success) {
            console.error('🔐 AUTH CONFIRM: OAuth callback failed:', result.error);
            
            // Sign out the OAuth session since validation failed
            await supabase.auth.signOut();
            
            // Store error in sessionStorage so signup page can display it
            const errorMessage = result.needsVerification 
              ? `Please verify your email before continuing. We've sent a verification link to ${result.email}`
              : result.error || 'Authentication failed';
            
            sessionStorage.setItem('oauth_error', errorMessage);
            sessionStorage.setItem('oauth_error_email', result.email || '');
            
            // Redirect back to signup/signin page based on intended action
            const redirectPath = intendedAction === 'signup' ? '/signup' : '/signin';
            console.log('🔐 AUTH CONFIRM: Redirecting to', redirectPath, 'with error');
            window.location.href = redirectPath;
            return;
          }
          
          console.log('🔐 AUTH CONFIRM: OAuth validation successful');
          
          // Success! Clear any password reset flags
          sessionStorage.removeItem('mandatory_password_reset');
          sessionStorage.removeItem('password_reset_in_progress');
          sessionStorage.removeItem('recovery_token_hash');
          sessionStorage.removeItem('recovery_session_id');
          sessionStorage.removeItem('reset_token_hash');
          sessionStorage.removeItem('reset_target_email');
          
          // CRITICAL FIX: For SIGN-IN users, mark onboarding as complete
          // This prevents the onboarding wizard from triggering for returning users
          if (intendedAction === 'signin') {
            const { data: { session: signInSession } } = await supabase.auth.getSession();
            if (signInSession?.user?.id) {
              console.log('🔐 AUTH CONFIRM: Sign-in flow (new session) - marking onboarding complete to skip wizard');
              await markOnboardingCompleteForSignin(signInSession.user.id);
            }
          }
          
          console.log('🔐 AUTH CONFIRM: OAuth successful - verifying session before redirect');
          console.log('🚨 CACHE CHECK: This is NEW CODE v2.1 - timestamp:', Date.now());
          await logEvent({
            level: 'info',
            action: 'oauth_redirect_prepare',
            details: { message: 'OAuth successful - verifying session before redirect', cacheVersion: 'v2', timestamp: Date.now() }
          });
          
          // CRITICAL: Wait for session to be fully established and stored
          // This prevents race condition with AppStateManager
          let sessionVerified = false;
          for (let i = 0; i < 5; i++) {
            const { data: { session: verifySession } } = await supabase.auth.getSession();
            if (verifySession) {
              sessionVerified = true;
              console.log('🔐 AUTH CONFIRM: Session verified - safe to redirect');
              await logEvent({
                level: 'info',
                action: 'oauth_session_verified',
                details: { attempt: i + 1, message: 'Session verified - safe to redirect' }
              });
              break;
            }
            console.log(`🔐 AUTH CONFIRM: Waiting for session storage (attempt ${i + 1}/5)`);
            await logEvent({
              level: 'debug',
              action: 'oauth_session_wait',
              details: { attempt: i + 1, message: `Waiting for session storage (attempt ${i + 1}/5)` }
            });
            await new Promise(resolve => setTimeout(resolve, 200));
          }
          
          if (!sessionVerified) {
            console.warn('🔐 AUTH CONFIRM: Session not verified but proceeding anyway');
            await logEvent({
              level: 'warn',
              action: 'oauth_session_not_verified',
              details: { message: 'Session not verified but proceeding anyway' }
            });
          }
          
          await logEvent({
            level: 'info',
            action: 'oauth_redirecting_to_agent',
            details: { message: 'Redirecting to agent with full page reload' }
          });
          
          // Set flag to tell AppStateManager we just completed OAuth
          // This prevents immediate redirect back to login during session loading
          sessionStorage.setItem('oauth_just_completed', Date.now().toString());
          collapseSidebarForPostLogin();
          
          // Clean up OAuth in-progress flags
          sessionStorage.removeItem('oauth_in_progress');
          sessionStorage.removeItem('oauth_provider');
          sessionStorage.removeItem('oauth_start_time');
          
          console.log('🔐 AUTH CONFIRM: Set OAuth completion flag for AppStateManager');
          
          // Check for pending redirect before defaulting to agent
          const redirectPath = sessionStorage.getItem('redirect_after_login');
          
          console.log('🔍 [REDIRECT CHECK] OAuth completed - checking redirect:', {
            hasRedirectPath: !!redirectPath,
            redirectPath: redirectPath,
            intendedAction: intendedAction,
            allSessionStorageKeys: Object.keys(sessionStorage),
            sessionStorageContent: JSON.stringify(Object.fromEntries(Object.keys(sessionStorage).map(k => [k, sessionStorage.getItem(k)])))
          });

          // Always fire welcome email for signups BEFORE deciding where to redirect.
          // Uses fetch with keepalive:true so the request survives page navigation.
          if (intendedAction === 'signup') {
            const { data: { session: signupSession } } = await supabase.auth.getSession();
            if (signupSession?.user?.email) {
              console.log('[Email Funnel] Signup detected — firing welcome email to:', signupSession.user.email);
              fireWelcomeEmail(
                signupSession.user.id,
                signupSession.user.email,
                localStorage.getItem('i18nextLng')?.split('-')[0] || 'en',
              );
              // TASK 7-2 — best-effort referral attribution.
              try { await attributeSignupIfPending(); } catch { /* noop */ }
              try { await attributeOutreachSignupIfPending(); } catch { /* noop */ }
            }
          }

          if (redirectPath) {
            console.log('🔐 AUTH CONFIRM: ✅ Found redirect_after_login, redirecting to:', redirectPath);
            sessionStorage.removeItem('redirect_after_login');
            // For signups, always go through consent first (welcome email already fired above)
            if (intendedAction === 'signup') {
              if (redirectPath !== '/profile' && !isGenericPostAuthLanding(redirectPath)) {
                sessionStorage.setItem('redirect_after_login', redirectPath);
              }
              window.location.replace('/signup/consent');
            } else {
              window.location.replace(resolvePostAuthRedirect(redirectPath));
            }
          } else if (intendedAction === 'signup') {
            // New signup: mandatory Privacy Policy Consent before agent landing
            console.log('🔐 AUTH CONFIRM: Signup flow - redirecting to Privacy Consent');
            window.location.replace('/signup/consent');
          } else {
            console.log('🔐 AUTH CONFIRM: ⚠️ No redirect path found - defaulting to agent');
            window.location.replace(POST_AUTH_LANDING_PATH);
          }
          return;
        }
        
        // Check if we have a valid recovery flow or should redirect
        if (!isRecoveryFlow && !isOAuthCallback) {
          const isEmailSignup = type === 'signup' || type === 'email';

          // For email/password signup confirmation, the token_hash must be exchanged for a session first.
          // Without this step getSession() returns null and the user sees an error screen.
          if (isEmailSignup && tokenHash) {
            console.log('🔐 AUTH CONFIRM: Email signup confirmation — exchanging token_hash for session');
            try {
              const { error: otpError } = await supabase.auth.verifyOtp({
                token_hash: tokenHash,
                type: 'signup',
              });
              if (otpError) {
                console.warn('🔐 AUTH CONFIRM: verifyOtp failed, trying exchangeCodeForSession:', otpError.message);
                // Fallback: some Supabase versions use PKCE exchange
                await supabase.auth.exchangeCodeForSession(window.location.href).catch(() => {});
              } else {
                console.log('🔐 AUTH CONFIRM: verifyOtp succeeded');
              }
            } catch (exchErr) {
              console.warn('🔐 AUTH CONFIRM: token exchange error (non-fatal):', exchErr);
            }
          }

          // Now check for the session (either pre-existing or just exchanged above)
          const { data: { session: finalCheck } } = await supabase.auth.getSession();
          
          if (finalCheck) {
            console.log('🔐 AUTH CONFIRM: Found session on final check - redirecting');
            sessionStorage.removeItem('mandatory_password_reset');
            sessionStorage.removeItem('password_reset_in_progress');
            sessionStorage.removeItem('recovery_token_hash');

            if (isEmailSignup && finalCheck.user?.email) {
              console.log('🔐 AUTH CONFIRM: Email signup confirmed — triggering welcome email to:', finalCheck.user.email);
              fireWelcomeEmail(
                finalCheck.user.id,
                finalCheck.user.email,
                localStorage.getItem('i18nextLng')?.split('-')[0] || 'en',
              );
              // TASK 7-2 — best-effort referral attribution.
              try { await attributeSignupIfPending(); } catch { /* noop */ }
              try { await attributeOutreachSignupIfPending(); } catch { /* noop */ }
            }
            
            collapseSidebarForPostLogin();

            // Check for pending redirect
            const redirectPath = sessionStorage.getItem('redirect_after_login');
            if (redirectPath) {
              console.log('🔐 AUTH CONFIRM: Redirecting to:', redirectPath);
              sessionStorage.removeItem('redirect_after_login');
              window.location.href = resolvePostAuthRedirect(redirectPath);
            } else if (isEmailSignup) {
              // New email signup → go through consent page like OAuth signups
              console.log('🔐 AUTH CONFIRM: Email signup — redirecting to Privacy Consent');
              window.location.replace('/signup/consent');
            } else {
              window.location.href = POST_AUTH_LANDING_PATH;
            }
            return;
          }
          
          // No session, no valid flow - show error
          const hadOAuthIntent = !!localStorage.getItem('oauth_intended_action');
          if (hadOAuthIntent) {
            localStorage.removeItem('oauth_intended_action');
            sessionStorage.removeItem('oauth_in_progress');
            sessionStorage.removeItem('oauth_provider');
          }
          console.error('🔐 AUTH CONFIRM: No valid authentication flow detected', { hadOAuthIntent, isEmailSignup, hasTokenHash: !!tokenHash });
          if (isMounted) {
            setStatus('error');
            setError(hadOAuthIntent
              ? 'Sign-in could not be completed. The sign-in window may have been closed early or the link was not fully received. Please try again.'
              : 'Invalid authentication callback. Please try logging in again.');
            setFlowType(hadOAuthIntent ? 'oauth' : 'unknown');
          }
          return;
        }
        
        // From here, we know it's a password recovery flow - show UI
        if (isMounted) {
          setFlowType('recovery');
        }
        
        console.log('🔐 AUTH CONFIRM: Password recovery flow - showing UI')
        
        // Set password reset flags for recovery flows
        console.log('🔐 AUTH CONFIRM: Setting password reset security flags');
        sessionStorage.setItem('mandatory_password_reset', 'true');
        sessionStorage.setItem('password_reset_in_progress', 'true');
        if (tokenHash) {
          sessionStorage.setItem('recovery_token_hash', tokenHash);
        }
        
        // Password recovery flow - process it
        if (isRecoveryFlow && tokenHash) {
          // Password recovery flow
          console.log('🔐 AUTH CONFIRM: Processing password recovery flow');
          
          // Try to establish session first (this might fail in WebContainer, which is OK)
          let sessionEstablished = false;
          
          try {
            // Try PKCE exchange first
            console.log('🔐 AUTH CONFIRM: Attempting PKCE exchange');
            const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.href);
            
            if (!error && data.session) {
              console.log('🔐 AUTH CONFIRM: PKCE exchange successful');
              sessionEstablished = true;
            } else {
              console.log('🔐 AUTH CONFIRM: PKCE exchange failed (expected in some environments):', error?.message);
            }
          } catch (pkceError) {
            console.log('🔐 AUTH CONFIRM: PKCE exchange error (expected in some environments):', pkceError);
          }
          
          // Check for existing session
          if (!sessionEstablished) {
            try {
              const { data: { session }, error } = await supabase.auth.getSession();
              if (!error && session) {
                console.log('🔐 AUTH CONFIRM: Found existing session');
                sessionEstablished = true;
              }
            } catch {
              console.log('🔐 AUTH CONFIRM: No existing session found');
            }
          }
          
          // Password recovery flow - proceed to reset page
          console.log('🔐 AUTH CONFIRM: Proceeding to reset page', {
            sessionEstablished,
            hasToken: !!tokenHash
          });
          
          // Try to extract email from any existing session or user context
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user?.email) {
              sessionStorage.setItem('reset_target_email', session.user.email);
              console.log('🔐 AUTH CONFIRM: Stored target email for reset');
            }
          } catch {
            console.log('🔐 AUTH CONFIRM: Could not extract email (non-critical)');
          }
          
          if (isMounted) {
            setStatus('success');
            
            await logEvent({
              level: 'info',
              action: 'password_reset_token_exchanged',
              details: { 
                tokenHash: tokenHash?.substring(0, 20),
                sessionEstablished
              }
            });
            
            // Redirect to reset password page after short delay
            setTimeout(() => {
              if (isMounted) {
                console.log('🔐 AUTH CONFIRM: Redirecting to reset password page');
                window.location.href = '/reset-password';
              }
            }, 1500);
          }
        }
        
      } catch (err) {
        console.error('🔐 AUTH CONFIRM: Error:', err);
        
        if (isMounted) {
          setStatus('error');
          setError(err instanceof Error ? err.message : 'An unexpected error occurred');
          
          await logEvent({
            level: 'error',
            action: 'password_reset_token_exchange_failed',
            details: { 
              error: err instanceof Error ? err.message : 'Unknown error',
              url: window.location.href
            }
          });
        }
      }
    };
    
    // Small delay to ensure page is fully loaded
    const timer = setTimeout(handleAuthConfirm, 500);
    
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, []);

  // OAuth or processing: show branded card until redirect completes
  if (flowType === 'oauth' || (status === 'processing' && flowType === 'unknown')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 vscode-bg-primary vscode-transition">
        <Helmet>
          <title>Signing In - GoGrabJob</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div
          className="rounded-2xl shadow-2xl w-full max-w-[340px] overflow-hidden flex flex-col"
          style={{ animation: 'fadeInUp 0.28s ease-out', border: '1px solid var(--vscode-border-primary)' }}
        >
          {/* Gradient title bar */}
          <div
            className="flex items-center gap-3 px-5 py-4 shrink-0"
            style={{ background: 'var(--brand-cta-bg)' }}
          >
            <div className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center shadow-sm shrink-0">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-[0.15em] leading-none mb-1">GoGrabJob</p>
              <h2 className="text-base font-bold text-white leading-tight">
                {t('views.authConfirm.signingIn', 'Signing you in…')}
              </h2>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 py-5 flex flex-col items-center gap-4 vscode-bg-secondary">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-[#6C47FF]/20" />
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#6C47FF] animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#6C47FF]" />
              </div>
            </div>
            <div className="space-y-2 w-full">
              <div className="flex items-center gap-2.5">
                <div className="w-3.5 h-3.5 rounded-full border-2 border-[#6C47FF]/30 border-t-[#6C47FF] animate-spin shrink-0" />
                <span className="text-sm vscode-text-primary">Verifying your credentials</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-3.5 h-3.5 rounded-full border-2 border-[#6C47FF]/30 border-t-[#6C47FF] animate-spin shrink-0" style={{ animationDelay: '150ms' }} />
                <span className="text-sm vscode-text-primary">Preparing your workspace</span>
              </div>
            </div>
            <p className="text-xs text-center leading-relaxed vscode-text-secondary">
              Almost there — setting up your personalised job search workspace.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <Helmet>
        <title>
          {flowType === 'recovery' ? 'Confirming Reset Link' : 'Authenticating'} - GoGrabJob
        </title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <motion.div
        className="max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700 p-8">
          {status === 'processing' && (
            <>
              <motion.div
                className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                animate={{ 
                  scale: [1, 1.1, 1],
                  opacity: [0.7, 1, 0.7]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <LoadingSpinner size="md" showText={false} />
              </motion.div>
              
              <h1 className="text-2xl font-bold text-white mb-4">
                {t('views.authConfirm.processingResetLink')}
              </h1>
              <p className="text-gray-300 mb-6">
                Please wait while we verify your password reset link...
              </p>
              
              <div className="bg-gray-700/50 p-4 rounded-lg border border-gray-600">
                <div className="flex items-center justify-center space-x-2">
                  <Shield className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-400 text-sm">
                    Secure token exchange in progress
                  </span>
                </div>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <motion.div
                className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <CheckCircle className="w-8 h-8 text-green-400" />
              </motion.div>
              
              <h1 className="text-2xl font-bold text-white mb-4">
                {t('views.authConfirm.loginSuccessful')}
              </h1>
              <p className="text-gray-300 mb-6">
                Welcome back! Redirecting to your workspace...
              </p>
              
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center justify-center space-x-2">
                  <Lock className="w-4 h-4 text-green-400" />
                  <span className="text-green-300 text-sm">Secure session established</span>
                </div>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <motion.div
                className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </motion.div>
              
              <h1 className="text-2xl font-bold text-white mb-4">
                {flowType === 'recovery' ? 'Reset Link Invalid' : 'Sign-in could not be completed'}
              </h1>
              <p className="text-red-300 mb-6">
                {error}
              </p>
              
              {flowType === 'recovery' && (
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-yellow-400 font-medium text-sm">Common Causes</h4>
                      <ul className="text-yellow-300 text-xs mt-1 space-y-1">
                        <li>• Reset link was already used</li>
                        <li>• Link has expired (links expire after 1 hour)</li>
                        <li>• Page was refreshed during processing</li>
                        <li>• Multiple reset requests were made</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {flowType === 'oauth' && (
                <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 mb-6">
                  <p className="text-amber-200 text-sm">Complete the sign-in in the Google window without closing it, or try again from the login page.</p>
                </div>
              )}
              
              <div className="space-y-3">
                {flowType === 'recovery' && (
                  <button
                    onClick={() => onNavigate?.('forgot-password')}
                    className="w-full bg-cyan-500 hover:bg-cyan-400 text-gray-900 font-semibold py-3 px-4 rounded-lg transition-colors"
                  >
                    Get New Reset Link
                  </button>
                )}
                <button
                  onClick={() => onNavigate?.('login')}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  {flowType === 'oauth' ? 'Try again' : 'Back to Login'}
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AuthConfirmPage;
